import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType } from '../types/auth';
import { authAPI } from '../services/api';

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state by checking cookie-based authentication
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Try to get current user - this will work if authentication cookie exists
        const userData = await authAPI.getCurrentUser();
        setUser(userData);
        setToken('authenticated'); // Set a dummy token to indicate authenticated state
      } catch {
        // Not authenticated - user and token remain null
        setUser(null);
        setToken(null);
      }

      setIsLoading(false);
    };

    // Add a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setUser(null);
      setToken(null);
      setIsLoading(false);
    }, 2000); // 2 second timeout

    initializeAuth().finally(() => {
      clearTimeout(timeoutId);
    });
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });
      const { user: userData } = response;

      setToken('authenticated');
      setUser(userData);
    } catch (error) {
      throw error;
    }
  };

  const register = async (email: string, username: string, password: string) => {
    try {
      const response = await authAPI.register({ email, username, password });
      const { user: userData } = response;

      setToken('authenticated');
      setUser(userData);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } finally {
      setToken(null);
      setUser(null);
    }
  };

  const googleAuth = () => {
    authAPI.googleAuth();
  };

  const handleOAuthCallback = async () => {
    try {
      // Get user information - cookie should be set by OAuth callback
      const userResponse = await authAPI.getCurrentUser();
      
      // Update auth context
      setToken('authenticated');
      setUser(userResponse);
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    register,
    logout,
    googleAuth,
    handleOAuthCallback,
    refreshUser,
    setUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
